import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { weeklyPlannerAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import ReturnToDashboardPill from '@/components/ReturnToDashboardPill';
import useDeepLinkParams from '@/hooks/useDeepLinkParams';

/**
 * Metricool Scheduler — formerly "Weekly Planner".
 *
 * Asset scheduler. NEVER generates video here. Founder/admin-only.
 *
 * Workflow:
 *  1. Founder imports posts from Run My Business → /api/weekly-planner/items/import-from-rmb.
 *  2. Founder attaches an existing Ready video from My Projects (or an image)
 *     to each planner item.
 *  3. Founder sets a future scheduled_at.
 *  4. Founder clicks "Send to Metricool" — reuses the existing metricool_service
 *     send path. Backend refuses anything without playable media + future
 *     schedule.
 */

const STATUS_BADGE_VARIANT = {
  unscheduled_draft: 'secondary',
  needs_media: 'destructive',
  video_not_ready: 'destructive',
  ready_for_metricool: 'default',
  scheduled: 'default',
  sent: 'outline',
  send_failed: 'destructive',
};

const STATUS_LABEL = {
  unscheduled_draft: 'Unscheduled draft',
  needs_media: 'Needs media',
  video_not_ready: 'Video not ready',
  ready_for_metricool: 'Ready for Metricool',
  scheduled: 'Scheduled',
  sent: 'Sent to Metricool',
  send_failed: 'Send failed',
};

const MetricoolSchedulerPage = () => {
  const navigate = useNavigate();
  const deepLink = useDeepLinkParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerItem, setPickerItem] = useState(null);
  const [pickerVideos, setPickerVideos] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [imageDialogItem, setImageDialogItem] = useState(null);
  const [imageUrl, setImageUrl] = useState('');

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await weeklyPlannerAPI.listItems();
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load planner items');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Round 95 — deep-link acceptance from <TaskActionButton />.
  useEffect(() => {
    if (deepLink.hasDeepLink) {
      console.info('[MetricoolScheduler] deep-link accepted', deepLink);
    }
  }, [deepLink.hasDeepLink, deepLink.asset_id, deepLink.task_id]);

  const openVideoPicker = async (item) => {
    setPickerItem(item);
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      const data = await weeklyPlannerAPI.listAvailableVideos();
      setPickerVideos(data.videos || []);
    } catch (err) {
      toast.error('Could not load videos');
    } finally {
      setPickerLoading(false);
    }
  };

  const attachVideo = async (contentId) => {
    try {
      await weeklyPlannerAPI.attachVideo(pickerItem.id, contentId);
      toast.success('Video attached');
      setPickerOpen(false);
      setPickerItem(null);
      loadItems();
    } catch (err) {
      const det = err?.response?.data?.detail || {};
      toast.error(det.message || det.reason || 'Attach failed');
    }
  };

  const attachImage = async () => {
    if (!imageUrl.trim()) {
      toast.error('Provide a URL');
      return;
    }
    try {
      await weeklyPlannerAPI.attachImage(imageDialogItem.id, imageUrl.trim());
      toast.success('Image attached');
      setImageDialogItem(null);
      setImageUrl('');
      loadItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Attach failed');
    }
  };

  const setScheduledAt = async (item, isoValue) => {
    try {
      await weeklyPlannerAPI.patch(item.id, { scheduled_at: isoValue });
      toast.success('Scheduled');
      loadItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Could not set schedule');
    }
  };

  const sendToMetricool = async (item) => {
    try {
      const res = await weeklyPlannerAPI.sendToMetricool(item.id);
      if (res.ok) {
        toast.success('Sent to Metricool');
      } else {
        toast.error('Metricool send failed — check details on the card.');
      }
      loadItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Send failed');
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await weeklyPlannerAPI.delete(item.id);
      toast.success('Deleted');
      loadItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || 'Delete failed');
    }
  };

  // YYYY-MM-DDTHH:MM (datetime-local) → ISO with offset preserved as UTC.
  const localToIso = (local) => {
    if (!local) return null;
    const d = new Date(local);
    return d.toISOString();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 px-6 py-8" data-testid="metricool-scheduler-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Round 95 — Return pill visible when arriving via deep link */}
        <div className="flex items-center justify-between">
          <ReturnToDashboardPill testId="scheduler-return-pill" />
          {deepLink.hasDeepLink && (
            <span
              className="text-xs text-slate-500"
              data-testid="scheduler-deep-link-indicator"
              data-workflow-task-id={deepLink.task_id || ''}
              data-workflow-asset-id={deepLink.asset_id || ''}
              data-workflow-mode={deepLink.mode || ''}
              data-workflow-asset-type={deepLink.asset_type || ''}
            >
              Deep-linked from your Dashboard task
            </span>
          )}
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="scheduler-title">
              Metricool Scheduler
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Asset scheduler for finished posts &amp; videos. Imports from Run My Business,
              attaches Ready videos from My Projects, and pushes to Metricool — never
              generates new video here.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/bots/content')}
              data-testid="scheduler-back-to-content-bot"
            >
              Back
            </Button>
            <Button onClick={loadItems} data-testid="scheduler-refresh-btn">
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-slate-400 text-sm" data-testid="scheduler-loading">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div
            className="border border-dashed border-slate-700 rounded-xl p-8 text-center text-slate-400"
            data-testid="scheduler-empty"
          >
            No planner items yet. Import posts from Run My Business or click
            <span className="text-emerald-400"> Use in Weekly Planner </span>on a finished
            video in My Projects.
          </div>
        ) : (
          <div className="space-y-3" data-testid="scheduler-items-list">
            {items.map((item) => (
              <ItemCard
                key={item.id}
                item={item}
                onPickVideo={() => openVideoPicker(item)}
                onPickImage={() => { setImageDialogItem(item); setImageUrl(''); }}
                onSetSchedule={(v) => setScheduledAt(item, localToIso(v))}
                onSend={() => sendToMetricool(item)}
                onDelete={() => deleteItem(item)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Video picker dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle data-testid="video-picker-title">Pick a Ready Video</DialogTitle>
            <DialogDescription>
              Only `completed` videos with a playable audio preview are shown. Same video
              can be reused on multiple planner items.
            </DialogDescription>
          </DialogHeader>
          {pickerLoading ? (
            <div className="text-sm text-slate-400">Loading videos…</div>
          ) : pickerVideos.length === 0 ? (
            <div className="text-sm text-slate-400" data-testid="video-picker-empty">
              No Ready videos yet. Generate one from Content Bot first.
            </div>
          ) : (
            <div className="max-h-96 overflow-auto space-y-2" data-testid="video-picker-list">
              {pickerVideos.map((v) => (
                <button
                  key={v.id}
                  onClick={() => attachVideo(v.id)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-slate-800 border border-slate-700"
                  data-testid={`video-picker-item-${v.id}`}
                >
                  <div className="font-medium text-sm">{v.title || 'Untitled'}</div>
                  <div className="text-xs text-slate-400 truncate">{v.video_url}</div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image attach dialog */}
      <Dialog open={!!imageDialogItem} onOpenChange={(open) => !open && setImageDialogItem(null)}>
        <DialogContent className="bg-slate-900 text-slate-100">
          <DialogHeader>
            <DialogTitle>Attach Image / Asset</DialogTitle>
            <DialogDescription>Paste a public image URL (e.g. an existing thumbnail).</DialogDescription>
          </DialogHeader>
          <Input
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.png"
            data-testid="attach-image-url-input"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setImageDialogItem(null)}>
              Cancel
            </Button>
            <Button onClick={attachImage} data-testid="attach-image-confirm-btn">
              Attach
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ItemCard = ({ item, onPickVideo, onPickImage, onSetSchedule, onSend, onDelete }) => {
  const [localSched, setLocalSched] = useState(
    item.scheduled_at ? new Date(item.scheduled_at).toISOString().slice(0, 16) : '',
  );
  const status = item.status || 'unscheduled_draft';
  const variant = STATUS_BADGE_VARIANT[status] || 'secondary';
  const label = STATUS_LABEL[status] || status;
  const hasVideo = !!item.attached_video?.video_url;
  const hasImage = !!item.attached_image?.url;
  const canSend = status === 'ready_for_metricool';

  return (
    <div
      className="border border-slate-700 rounded-xl p-4 bg-slate-900/60"
      data-testid={`planner-item-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant={variant} data-testid={`planner-item-status-${item.id}`}>
              {label}
            </Badge>
            <span className="text-xs text-slate-500 uppercase tracking-wide">
              {item.platform || '—'} · {item.content_type || 'post'}
            </span>
          </div>
          <div className="text-sm text-slate-200 line-clamp-3" data-testid={`planner-item-caption-${item.id}`}>
            {item.caption || <em className="text-slate-500">no caption</em>}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            source: {item.source} · id: {item.source_id || item.id}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {hasVideo && (
              <span className="text-emerald-400" data-testid={`planner-item-video-attached-${item.id}`}>
                ✓ video attached
              </span>
            )}
            {hasImage && (
              <span className="text-emerald-400" data-testid={`planner-item-image-attached-${item.id}`}>
                ✓ image attached
              </span>
            )}
            {!hasVideo && !hasImage && (
              <span className="text-rose-400">no media</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-2 min-w-[180px]">
          <Button
            variant="outline"
            size="sm"
            onClick={onPickVideo}
            data-testid={`planner-item-attach-video-btn-${item.id}`}
            disabled={status === 'sent'}
          >
            Attach existing video
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onPickImage}
            data-testid={`planner-item-attach-image-btn-${item.id}`}
            disabled={status === 'sent'}
          >
            Attach image
          </Button>
        </div>
      </div>
      <div className="mt-3 flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <label
            className="text-xs text-slate-400 block mb-1"
            htmlFor={`sched-${item.id}`}
          >
            Scheduled at (must be in the future)
          </label>
          <Input
            id={`sched-${item.id}`}
            type="datetime-local"
            value={localSched}
            onChange={(e) => setLocalSched(e.target.value)}
            disabled={status === 'sent'}
            data-testid={`planner-item-schedule-input-${item.id}`}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onSetSchedule(localSched)}
          disabled={!localSched || status === 'sent'}
          data-testid={`planner-item-save-schedule-btn-${item.id}`}
        >
          Save date
        </Button>
        <Button
          size="sm"
          onClick={onSend}
          disabled={!canSend}
          data-testid={`planner-item-send-to-metricool-btn-${item.id}`}
        >
          Send to Metricool
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={status === 'sent'}
          data-testid={`planner-item-delete-btn-${item.id}`}
        >
          Delete
        </Button>
      </div>
      {item.metricool_send && status === 'send_failed' && (
        <div
          className="mt-2 text-xs text-rose-400"
          data-testid={`planner-item-send-error-${item.id}`}
        >
          {item.metricool_send?.error || 'Metricool send failed.'}
        </div>
      )}
    </div>
  );
};

export default MetricoolSchedulerPage;
